import React, { FC, useEffect, useState } from 'react';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import { languageName } from '~/lib/translations/languageName';
import ArrowOrderIcon from '~/assets/icons/arrow-order.svg';
import { resolveLocale } from '~/ui/locale-selector/LocaleSelector';
import { Overlay } from '~/ui/overlay/Overlay';
import * as S from './LargeLocaleSelector.styled';
import { TextLocalePage } from '~/training/settings/locales/TextLocalePage';
import { Flex } from '~/ui/flex/Flex';
import { SettingsPages, useSettingsStore } from '~/training/settings/Store';
import { getSupportedLanguageLabel } from '~/lib/translations/getSupportedLanguageLabel';
import {
  fetchLearnerLanguagePreference,
  logLmsLanguage,
  useLmsAdapter
} from '~/lms';
import { getLanguagePickerLocales } from '~/training/settings/locales/getLanguagePickerLocales';

const selector = (s: TrainingState) =>
  select(
    s,
    'setTextLocale',
    'setLocale',
    'textLocale',
    'textLocales',
    'subtitleLocales',
    'mediumLocales',
    'supportedLanguages',
    'languageAutoDetect'
  );

export const LargeLocaleSelector: FC<{
  locales: string[];
  reloadSubtitle?: boolean;
  isOnboarding?: boolean;
}> = ({ locales, reloadSubtitle, isOnboarding }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    textLocale,
    setTextLocale,
    setLocale,
    textLocales,
    subtitleLocales,
    mediumLocales,
    supportedLanguages,
    languageAutoDetect
  } = useTrainingStore(selector);
  const { open, setPage } = useSettingsStore();
  const lms = useLmsAdapter();
  // On onboarding, don't seed the dropdown from a stale API `textLocale` —
  // wait for the LMS read (or fall back after it completes).
  const [selectedLocale, setSelectedLocale] = useState(() =>
    isOnboarding ? 'en' : textLocale || 'en'
  );
  const selectableLocales = getLanguagePickerLocales({
    textLocales,
    subtitleLocales,
    mediumLocales
  });

  // Keep dropdown label in sync with the store (settings changes, etc.).
  // On onboarding we skip this so a stale API `en` does not overwrite LMS `fr`.
  useEffect(() => {
    if (isOnboarding) return;
    setSelectedLocale(textLocale);
  }, [textLocale, isOnboarding]);

  useEffect(() => {
    const applyLocale = (locale: string) => {
      setSelectedLocale(locale);
      document.documentElement.setAttribute('lang', locale);

      if (isOnboarding) {
        // Match manual pick in TextLocalePage (fullSelector → setLocale) and
        // update textLocale immediately so translations load before the API
        // round-trip. LMS locale wins over a stale server value (e.g. `en`).
        useTrainingStore.setState({ textLocale: locale });
        setLocale(locale);
      } else if (locale !== textLocale) {
        setTextLocale(locale, reloadSubtitle ?? false, false);
      }
    };

    const applyBrowserFallback = () => {
      const detected = languageAutoDetect
        ? window.navigator.language
        : textLocale || 'en';

      let resolved = resolveLocale(detected, locales);

      if (
        languageAutoDetect &&
        textLocale &&
        resolved === 'en' &&
        detected !== textLocale
      ) {
        const fromProfile = resolveLocale(textLocale, locales);
        if (locales.includes(fromProfile)) {
          resolved = fromProfile;
        }
      }

      const finalLocale = locales.includes(resolved)
        ? resolved
        : textLocale && locales.includes(resolveLocale(textLocale, locales))
          ? resolveLocale(textLocale, locales)
          : 'en';

      applyLocale(finalLocale);
    };

    const applyOnboardingApiFallback = () => {
      const fromApi = textLocale
        ? resolveLocale(textLocale, selectableLocales)
        : 'en';
      const fallback = selectableLocales.includes(fromApi) ? fromApi : 'en';
      logLmsLanguage('onboarding fallback to API locale', {
        textLocale,
        fallback
      });
      // UI only — server already has this locale; persist on Continue / manual pick.
      setSelectedLocale(fallback);
      document.documentElement.setAttribute('lang', fallback);
    };

    void (async () => {
      if (lms.isAvailable) {
        try {
          const externalLang = await fetchLearnerLanguagePreference(lms);
          const allowedLocales = isOnboarding ? selectableLocales : locales;

          logLmsLanguage('locale selector LMS result', {
            isOnboarding,
            externalLang: externalLang || '(empty)',
            allowedLocales
          });

          if (externalLang) {
            const resolved = resolveLocale(externalLang, allowedLocales);
            if (allowedLocales.includes(resolved)) {
              logLmsLanguage('onboarding apply LMS locale', {
                resolved,
                previousTextLocale: textLocale
              });
              applyLocale(resolved);
              return;
            }
            logLmsLanguage('LMS locale not in allowed list', {
              resolved,
              allowedLocales
            });
          }
        } catch (error) {
          logLmsLanguage('locale selector LMS read failed', {
            error: error instanceof Error ? error.message : String(error)
          });
        }
      }

      if (isOnboarding) {
        applyOnboardingApiFallback();
        return;
      }

      applyBrowserFallback();
    })();
  }, []);

  return (
    <>
      <S.Select
        onClick={() => {
          setIsModalOpen(true);
          setPage(SettingsPages.TextLocale);
        }}
      >
        <S.TextBlock>
          {languageName(selectedLocale)}

          <span>
            {getSupportedLanguageLabel(
              selectedLocale,
              supportedLanguages[selectedLocale]
            )}
          </span>
        </S.TextBlock>

        <ArrowOrderIcon />
      </S.Select>

      <Overlay open={isModalOpen} opacity={0.1}>
        <Flex
          zIndex="dialogs"
          p={{ _: 'sm', sm: 'xl' }}
          minHeight="100%"
          justifyContent={{ _: 'stretch', sm: 'center' }}
          alignItems={{ _: 'flex-end', sm: 'center' }}
          overflow="hidden"
          aria-modal={open}
        >
          <S.ModalBlock>
            <TextLocalePage
              onClose={() => setIsModalOpen(false)}
              centerTitle
              selectedLocale={selectedLocale}
              setSelectedLocale={
                setSelectedLocale as unknown as (value: string) => void
              }
            />
          </S.ModalBlock>
        </Flex>
      </Overlay>
    </>
  );
};
